import { memo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { VideoDialog } from "@/components/VideoDialog";

const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden pt-24 pb-12 bg-gradient-hero">
      {/* Animated mesh gradient background */}
      <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
      
      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 -left-32 w-[400px] h-[400px] bg-primary/30 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -right-32 w-[350px] h-[350px] bg-accent/25 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-secondary/20 rounded-full blur-[90px] animate-pulse" style={{ animationDelay: '2s' }} />

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          {/* Badge with proper spacing */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/30 backdrop-blur-xl border border-white/20 shadow-glass">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-medium text-foreground">{t('hero.badge')}</span>
          </div>

          {/* Heading */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight px-4" style={{ willChange: 'contents' }}>
            <span className="block mb-2 text-foreground">
              {t('hero.title.line1')}
            </span>
            <span className="block bg-gradient-primary bg-clip-text text-transparent">
              {t('hero.title.line2')}
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* Interactive Video Showcase */}
          <div 
            onClick={() => setVideoOpen(true)}
            className="group relative cursor-pointer max-w-sm mx-auto my-4 p-5 rounded-2xl bg-gradient-to-b from-primary/20 to-accent/10 border border-primary/30 hover:border-primary/60 transition-all duration-500 hover:scale-105 hover:shadow-[0_0_40px_rgba(var(--primary),0.3)]"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500" />
            <div className="relative space-y-3">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 backdrop-blur-xl border border-primary/40 mb-3 group-hover:bg-primary/30 transition-all duration-300">
                  <Play className="w-6 h-6 text-primary fill-primary group-hover:scale-110 transition-transform" />
                </div>
                <p className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent mb-1">
                  {t('hero.videoTitle')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('hero.videoSubtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* CTA Buttons with consistent spacing */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
            <Link to="/courses">
              <Button variant="hero" size="lg" className="group w-full sm:w-auto min-w-[180px]">
                {t('hero.browseCourses')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" size="lg" className="w-full sm:w-auto min-w-[180px]">
                {t('hero.learnMore')}
              </Button>
            </Link>
          </div>

          {/* Stats Grid */}
          <div className="pt-8 max-w-3xl mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.smart')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.smartDesc')}</div>
              </div>
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.live')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.liveDesc')}</div>
              </div>
              <div className="text-center p-4 md:p-5 rounded-xl bg-card/30 backdrop-blur-xl border border-white/10 shadow-glass hover:shadow-glass-hover transition-all duration-300">
                <div className="text-2xl md:text-3xl font-bold bg-gradient-primary bg-clip-text text-transparent mb-1 break-words">{t('hero.stats.pro')}</div>
                <div className="text-xs md:text-sm text-muted-foreground break-words">{t('hero.stats.proDesc')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade with proper positioning */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      
      <VideoDialog 
        open={videoOpen} 
        onOpenChange={setVideoOpen}
        videoId="I64IuEE1x2I"
        title={t('hero.videoTitle')}
      />
    </section>
  );
};

export default memo(Hero);
